import React, { useCallback } from 'react';
import Draggable from 'react-draggable';

import useRect from '@lyearn/core/hooks/useRect';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, Button, ClickAwayListener, Stack, Typography } from '@lyearn/ui';
import { Image } from '@/components/Image';

import { imageStyles, repositionLabelStyles } from '../BannerWithTitle/styles';
import { RepositioningBannerProps } from './types';

const RepositioningBanner: React.FC<RepositioningBannerProps> = (props) => {
  const {
    yOffsetPercent,
    onCancel,
    onSavePosition,
    setYOffsetPercent,
    imageSrc,
    naturalImageHeight,
  } = props;
  const { t } = useTranslation();

  const { ref: containerRef, height: containerHeight } = useRect<HTMLDivElement>();

  const repositionImage = useCallback(
    (e: any, data: any) => {
      const newImageOffsetPercent =
        containerHeight! <= naturalImageHeight
          ? (data.y * 100) / (containerHeight! - naturalImageHeight)
          : 50;
      setYOffsetPercent(newImageOffsetPercent);
    },
    [containerHeight, naturalImageHeight, setYOffsetPercent],
  );

  return (
    <ClickAwayListener onClickAway={onCancel}>
      <Box ref={containerRef} className="h-full w-full">
        <Button
          appearance="inverse"
          className="absolute top-12 z-10 shadow-01 end-12"
          size="small"
          variant="filled"
          onClick={onSavePosition}>
          {t('Common:Actions.Save')}
        </Button>
        <Stack
          alignItems="center"
          className={cn(
            'absolute z-10 h-32 w-192 rounded-8 bg-overlay-light',
            repositionLabelStyles,
          )}
          justifyContent="center">
          <Typography color="text-text-white" variant="body-short-03">
            {t('Course:Lesson.DragImageToReposition')}
          </Typography>
        </Stack>
        <Draggable
          bounds={{
            left: 0,
            right: 0,
            top:
              containerHeight && containerHeight <= naturalImageHeight
                ? containerHeight! - naturalImageHeight
                : 0,
            bottom: 0,
          }}
          defaultPosition={{
            x: 0,
            y:
              containerHeight && containerHeight <= naturalImageHeight
                ? (yOffsetPercent * naturalImageHeight) / 100
                : 0,
          }}
          onDrag={repositionImage}>
          <Image
            alt={t('Common:Design.Cover')}
            className="h-full w-full cursor-move"
            classes={{
              image: imageStyles(yOffsetPercent),
            }}
            draggable={false}
            layout="fill"
            src={imageSrc}
          />
        </Draggable>
      </Box>
    </ClickAwayListener>
  );
};

export default RepositioningBanner;
